import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import restClient from '../../services/rest';
import {
  MedicationStoreActionTypes,
  MedicationStore,
  GetMedicationSuccessAction,
  NoteActionStatus,
} from './types';
import { Medication } from '../../generated';
import axios from 'axios';
import { formatUrl, getRestHost, getCookie } from '../../shared/utils';

export const setMedicationNotesLoading = (isLoading: boolean) => ({
  type: MedicationStoreActionTypes.SET_MEDICATION_NOTES_LOADING,
  payload: isLoading,
});

export const setMedicationNoteActionStatus = (status: NoteActionStatus) => ({
  type: MedicationStoreActionTypes.SET_MEDICATION_NOTE_ACTION_STATUS,
  payload: status,
});

export const clearMedicationNoteActionStatus = () => ({
  type: MedicationStoreActionTypes.CLEAR_MEDICATION_NOTE_ACTION_STATUS,
});

export const setMedicationStatusHistoryLoading = (isLoading: boolean) => ({
  type: MedicationStoreActionTypes.SET_MEDICATION_STATUS_HISTORY_LOADING,
  payload: isLoading,
});

export const setUpdateMedicationStatusSuccess = (success: boolean) => ({
  type: MedicationStoreActionTypes.SET_UPDATE_MEDICATION_STATUS_SUCCESS,
  payload: success,
});

export const resetUpdateMedicationStatusSuccess = () => ({
  type: MedicationStoreActionTypes.RESET_UPDATE_MEDICATION_STATUS_SUCCESS,
});

export const fetchMedicationNotes = (ownerId: number, ndc: string) => {
  return async (dispatch: any) => {
    dispatch(setMedicationNotesLoading(true));
    try {
      const response = await axios.get(
        formatUrl(getRestHost(), `owners/${ownerId}/medication/${ndc}/note`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch({
        type: MedicationStoreActionTypes.FETCH_MEDICATION_NOTES_SUCCESS,
        payload: response.data,
      });
    } catch (err: any) {
      toast.error(
        err?.response?.data ||
          err.message ||
          'Failed to fetch medication notes',
      );
      dispatch(setMedicationNotesLoading(false));
    }
  };
};

export const addMedicationNote = (
  ownerId: number,
  ndc: string,
  note: string,
) => {
  return async (dispatch: any) => {
    dispatch(setMedicationNotesLoading(true));
    try {
      await axios.post(
        formatUrl(getRestHost(), `owners/${ownerId}/medication/${ndc}/note`),
        { note },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      dispatch(fetchMedicationNotes(ownerId, ndc));
    } catch (err: any) {
      toast.error(err?.response?.data || err.message || 'Failed to add note', {
        autoClose: 3000,
      });
    } finally {
      dispatch(setMedicationNotesLoading(false));
    }
  };
};

export const updateMedicationNote = (
  noteId: number,
  newNote: string,
  ownerId: number,
  ndc: string,
) => {
  return async (dispatch: any) => {
    dispatch(setMedicationNotesLoading(true));
    try {
      await axios.put(
        formatUrl(getRestHost(), `owners/medication_note/${noteId}`),
        { new_note: newNote },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        setMedicationNoteActionStatus({
          success: true,
          message: 'Note updated successfully',
          show: true,
        }),
      );
      dispatch(fetchMedicationNotes(ownerId, ndc));
    } catch (err: any) {
      dispatch(
        setMedicationNoteActionStatus({
          success: false,
          message:
            err?.response?.data || err.message || 'Failed to update note',
          show: true,
        }),
      );
    } finally {
      dispatch(setMedicationNotesLoading(false));
    }
  };
};

export const deleteMedicationNote = (
  noteId: number,
  ownerId: number,
  ndc: string,
) => {
  return async (dispatch: any) => {
    dispatch(setMedicationNotesLoading(true));
    try {
      await axios.delete(
        formatUrl(getRestHost(), `owners/medication_note/${noteId}`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        setMedicationNoteActionStatus({
          success: true,
          message: 'Note deleted successfully',
          show: true,
        }),
      );
      dispatch(fetchMedicationNotes(ownerId, ndc));
    } catch (err: any) {
      dispatch(
        setMedicationNoteActionStatus({
          success: false,
          message:
            err?.response?.data || err.message || 'Failed to delete note',
          show: true,
        }),
      );
    } finally {
      dispatch(setMedicationNotesLoading(false));
    }
  };
};

export const fetchMedicationStatusHistory = (ownerId: number, ndc: string) => {
  return async (dispatch: any) => {
    dispatch(setMedicationStatusHistoryLoading(true));
    try {
      const response = await axios.get(
        formatUrl(getRestHost(), `owners/${ownerId}/medication/${ndc}/history`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200 && Array.isArray(response.data)) {
        dispatch({
          type: MedicationStoreActionTypes.FETCH_MEDICATION_STATUS_HISTORY_SUCCESS,
          payload: response.data,
        });
      } else {
        dispatch({
          type: MedicationStoreActionTypes.FETCH_MEDICATION_STATUS_HISTORY_SUCCESS,
          payload: [],
        });
        toast.error('Failed to fetch medication status history', {
          autoClose: 3000,
        });
      }
    } catch (err: any) {
      dispatch({
        type: MedicationStoreActionTypes.FETCH_MEDICATION_STATUS_HISTORY_SUCCESS,
        payload: [],
      });
      toast.error(
        err?.response?.data ||
          err.message ||
          'Failed to fetch medication status history',
        { autoClose: 3000 },
      );
    } finally {
      dispatch(setMedicationStatusHistoryLoading(false));
    }
  };
};

export const updateMedicationStatus = (
  ownerId: number,
  ndc: string,
  drug_usage_status_id: number,
  change_reason: string,
) => {
  return async (dispatch: any) => {
    dispatch(setMedicationStatusHistoryLoading(true));
    try {
      await axios.put(
        formatUrl(getRestHost(), `owners/${ownerId}/medication`),
        {
          drug_usage_status_id,
          change_reason,
          ndc,
        },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      toast.success('Status updated successfully');
      dispatch(setUpdateMedicationStatusSuccess(true));
    } catch (err: any) {
      toast.error(
        err?.response?.data || err.message || 'Failed to update status',
      );
      dispatch(setUpdateMedicationStatusSuccess(false));
    } finally {
      dispatch(setMedicationStatusHistoryLoading(false));
    }
  };
};

function getMedicationSuccess(
  data: Array<Medication>,
): GetMedicationSuccessAction {
  return {
    type: MedicationStoreActionTypes.GET_MEDICATION_SUCCESS,
    payload: data,
  };
}

export const getMedications = (pharmacyId?: number) => {
  return async (
    dispatch: ThunkDispatch<MedicationStore, void, AnyAction> & Dispatch,
  ) => {
    try {
      const meds = await restClient.medications.listMedications(pharmacyId);

      dispatch(getMedicationSuccess(meds));
    } catch (err: any) {
      toast.error(err?.response?.data || err);
    }
  };
};
